import { Button } from '~/components/ui/Button';
import { WrapperWidget } from '@agentselly/ui';
import { useTranslate } from '@tolgee/react';
import { AppLink } from '~/components/AppLink';
import { useAuthz } from '~/context/AuthzContext';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Home,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Handshake,
} from 'lucide-react';
import { formatCurrency } from '~/utils/formatCurrency';
import { useBackendMutation } from '~/helper/useBackendMutation';
import { MutationReference } from '@agentselly/immodossier-shared';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppParams } from '~/helper/useAppParams';
import { invalidateEvaluationQueries } from '~/helper/invalidatePropertyQueries';

interface Props {
  readonly person?: {
    readonly isFemale: boolean | undefined;
    readonly firstname: string;
    readonly lastname: string;
    readonly email?: string;
    readonly phone?: string;
    readonly mobile?: string;
    readonly address?: string;
    readonly zip?: string;
    readonly city?: string;
    readonly photoUrl?: string;
  };
  readonly property?: {
    readonly id: string;
    readonly title?: string;
    readonly address?: string;
    readonly zip?: string;
    readonly city?: string;
    readonly category?: string;
    readonly numberOfRooms?: number;
    readonly price?: number;
    readonly evaluation?: {
      readonly sources?: {
        readonly iazi?: {
          readonly estimatedValue?: number;
          readonly estimatedLowerValue?: number;
          readonly estimatedUpperValue?: number;
        };
      };
      readonly timestamp?: string;
    };
  };
  readonly lead?: {
    readonly type: string;
    readonly status: string;
    readonly createdAt?: string;
    readonly updatedAt?: string;
  };
  readonly consultant?: {
    readonly photoUrl: string;
    readonly firstname: string;
    readonly lastname: string;
    readonly email?: string;
    readonly phone?: string;
    readonly mobile?: string;
  };
  readonly partner?: {
    readonly photoUrl?: string;
    readonly firstname?: string;
    readonly lastname?: string;
    readonly email?: string;
    readonly phone?: string;
    readonly mobile?: string;
  };
  readonly link?: string;
  readonly evaluationTrend?: {
    readonly evaluationValue: number;
    readonly evaluationDate?: string;
    readonly trendPercent: number;
    readonly previousValue?: number;
  } | null;
  readonly realEstateLocationId?: string;
}

export function CockpitWelcomeWidget(props: Props) {
  const queryClient = useQueryClient();
  const { dossierId, propertyId, contactId } = useAppParams();
  const { t } = useTranslate();
  const { atLeast, isPartner, isUser } = useAuthz();
  const showCustomerData = atLeast('internal');
  const canRefreshEvaluation = atLeast('partner');
  const firstname = props.person?.firstname;
  const lastname = props.person?.lastname;
  const consultantPhoto = props.consultant?.photoUrl
    ? `${props.consultant.photoUrl}`
    : '';
  const partnerPhoto = props.partner?.photoUrl || '';

  // Use evaluationTrend (from AdminDossier) or fallback to property.evaluation (from HubSpot REO)
  const initialEvaluationValue = props.evaluationTrend?.evaluationValue
    ? Number(props.evaluationTrend.evaluationValue)
    : props.property?.evaluation?.sources?.iazi?.estimatedValue
    ? Number(props.property.evaluation.sources.iazi.estimatedValue)
    : undefined;

  const initialEvaluationDate =
    props.evaluationTrend?.evaluationDate ||
    props.property?.evaluation?.timestamp;

  const initialLowValue = props.property?.evaluation?.sources?.iazi
    ?.estimatedLowerValue
    ? Number(props.property.evaluation.sources.iazi.estimatedLowerValue)
    : undefined;

  const initialHighValue = props.property?.evaluation?.sources?.iazi
    ?.estimatedUpperValue
    ? Number(props.property.evaluation.sources.iazi.estimatedUpperValue)
    : undefined;

  const [
    evaluationValue,
    setEvaluationValue,
  ] = useState<number | undefined>(initialEvaluationValue);
  const [
    evaluationDate,
    setEvaluationDate,
  ] = useState<string | undefined>(initialEvaluationDate);
  const [
    evaluationLow,
    setEvaluationLow,
  ] = useState<number | undefined>(initialLowValue);
  const [
    evaluationHigh,
    setEvaluationHigh,
  ] = useState<number | undefined>(initialHighValue);
  const [
    errorMessage,
    setErrorMessage,
  ] = useState<string | null>(null);
  const [
    previousEvaluation,
    setPreviousEvaluation,
  ] = useState<{ value: number; date: string } | null>(null);

  const refreshMutation = useBackendMutation<
    MutationReference.ImmoDossierEvaluate,
    { realEstateLocationId: string }
  >(MutationReference.ImmoDossierEvaluate, {
    onSuccess: async (data) => {
      setErrorMessage(null);
      if (data.iazi?.value) {
        // Save previous evaluation before updating
        if (evaluationValue && evaluationDate) {
          setPreviousEvaluation({
            value: evaluationValue,
            date: evaluationDate,
          });
        }
        setEvaluationValue(Number(data.iazi.value));
        setEvaluationDate(new Date().toISOString());
        // IAZI uses lowConfInt95/hiConfInt95 for the confidence interval
        if (data.iazi.lowConfInt95) {
          setEvaluationLow(Number(data.iazi.lowConfInt95));
        }
        if (data.iazi.hiConfInt95) {
          setEvaluationHigh(Number(data.iazi.hiConfInt95));
        }
        await invalidateEvaluationQueries(queryClient, {
          dossierId: dossierId ?? '',
          propertyId: propertyId ?? props.property?.id,
          contactId,
        });
      } else {
        setErrorMessage(
          'Keine Bewertung erhalten. Bitte später erneut versuchen.',
        );
      }
    },
    onError: (error) => {
      setErrorMessage(
        error?.message ||
          'Bewertung fehlgeschlagen. Bitte später erneut versuchen.',
      );
    },
  });

  const handleRefresh = () => {
    if (props.realEstateLocationId) {
      setErrorMessage(null);
      refreshMutation.mutate({
        realEstateLocationId: props.realEstateLocationId,
      });
    }
  };

  const salutation = {
    true: 'salutation_mrs',
    false: 'salutation_mr',
  }[String(props.person?.isFemale)];

  // Evaluation display component - inline style matching property info
  const EvaluationDisplay = () => {
    if (!canRefreshEvaluation) return null;

    // Has evaluation value - show it inline
    if (evaluationValue) {
      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 h-5">
            <span className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
              Schätzung:
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
              {formatCurrency(evaluationValue)}
            </span>
            {props.evaluationTrend?.trendPercent !== 0 &&
              props.evaluationTrend?.previousValue && (
                <span
                  className={`flex items-center gap-0.5 text-xs font-medium flex-shrink-0 ${
                    props.evaluationTrend.trendPercent > 0
                      ? 'text-emerald-600'
                      : props.evaluationTrend.trendPercent < 0
                      ? 'text-red-600'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {props.evaluationTrend.trendPercent > 0 ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : props.evaluationTrend.trendPercent < 0 ? (
                    <TrendingDown className="h-3 w-3" />
                  ) : (
                    <Minus className="h-3 w-3" />
                  )}
                  {props.evaluationTrend.trendPercent > 0 ? '+' : ''}
                  {props.evaluationTrend.trendPercent.toFixed(1)}%
                </span>
              )}
          </div>
          {evaluationLow && evaluationHigh && (
            <div className="flex items-center gap-2 h-5">
              <span className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                Spanne:
              </span>
              <span className="text-xs text-gray-700 dark:text-gray-200 dark:text-gray-300 truncate">
                {formatCurrency(evaluationLow)} -{' '}
                {formatCurrency(evaluationHigh)}
              </span>
            </div>
          )}
          {evaluationDate && (
            <div className="flex items-center gap-2 h-5">
              <span className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                Stand:
              </span>
              <span className="text-xs text-gray-700 dark:text-gray-200 dark:text-gray-300">
                {new Date(evaluationDate).toLocaleDateString('de-CH', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </span>
            </div>
          )}
          {previousEvaluation && (
            <div className="flex items-center gap-2 h-5">
              <span className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                Vorherige:
              </span>
              <span className="text-xs text-gray-700 dark:text-gray-200 dark:text-gray-300 truncate">
                {formatCurrency(previousEvaluation.value)} (
                {new Date(previousEvaluation.date).toLocaleDateString('de-CH', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
                )
              </span>
            </div>
          )}
          {!previousEvaluation && props.evaluationTrend?.previousValue && (
            <div className="flex items-center gap-2 h-5">
              <span className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                Vorherige:
              </span>
              <span className="text-xs text-gray-700 dark:text-gray-200 dark:text-gray-300 truncate">
                {formatCurrency(props.evaluationTrend.previousValue)}
              </span>
            </div>
          )}
          {errorMessage && (
            <div className="text-xs text-red-600">{errorMessage}</div>
          )}
        </div>
      );
    }

    // No evaluation - show message
    if (props.realEstateLocationId) {
      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2 h-5">
            <span className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
              Schätzung:
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Nicht vorhanden
            </span>
          </div>
          {errorMessage && (
            <div className="text-xs text-red-600">{errorMessage}</div>
          )}
        </div>
      );
    }

    // No realEstateLocationId
    return (
      <div className="flex items-center gap-2 h-5">
        <span className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
          Schätzung:
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          Nicht verfügbar
        </span>
      </div>
    );
  };

  // Unified view for all roles
  const hasConsultant =
    props.consultant?.firstname || props.consultant?.lastname;
  const hasPartner = props.partner?.firstname || props.partner?.lastname;
  const showCustomer = atLeast('partner'); // Show customer card for partner, internal, admin

  return (
    <WrapperWidget>
      <div className="flex flex-col h-full gap-y-5">
        {/* Personal Greeting */}
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">
            {t('cockpit_welcome_widget_title', {
              salutationOrFirstname: salutation ? t(salutation) : firstname,
              lastname: lastname,
            })}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('cockpit_welcome_widget_text')}
          </p>
        </div>

        {/* Consultants Row - Always show internal + external for all roles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Internal Consultant (AgentSelly) */}
          {hasConsultant ? (
            <div className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/20 border border-primary-200 dark:border-primary-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Handshake className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                <h3 className="text-sm font-semibold text-primary-800 dark:text-primary-200">
                  {isUser ? 'Ihr AgentSelly Berater' : 'AgentSelly Berater'}
                </h3>
              </div>
              <div className="flex items-start gap-3">
                {consultantPhoto ? (
                  <img
                    className="rounded-full w-14 h-14 border-2 border-white shadow-sm dark:shadow-gray-900/50 object-cover"
                    src={consultantPhoto}
                    alt={`${props.consultant?.firstname} ${props.consultant?.lastname}`}
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-primary-200 dark:bg-primary-800 flex items-center justify-center flex-shrink-0">
                    <User className="h-7 w-7 text-primary-600 dark:text-primary-400" />
                  </div>
                )}
                <div className="flex-1 space-y-1">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {props.consultant?.firstname} {props.consultant?.lastname}
                  </p>
                  {props.consultant?.email && (
                    <a
                      href={`mailto:${props.consultant.email}`}
                      className="flex items-center gap-1.5 text-sm text-primary-700 dark:text-primary-300 hover:text-primary-900 dark:hover:text-primary-100"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      <span className="truncate">{props.consultant.email}</span>
                    </a>
                  )}
                  {(props.consultant?.phone || props.consultant?.mobile) && (
                    <a
                      href={`tel:${
                        props.consultant.phone || props.consultant.mobile
                      }`}
                      className="flex items-center gap-1.5 text-sm text-primary-700 dark:text-primary-300 hover:text-primary-900 dark:hover:text-primary-100"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {props.consultant.phone || props.consultant.mobile}
                    </a>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-center justify-center">
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Kein interner Berater zugewiesen
              </p>
            </div>
          )}

          {/* External Partner */}
          {hasPartner ? (
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/20 border border-blue-200 dark:border-blue-700 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                  {isUser ? 'Ihr Valiant Berater' : 'Valiant Berater'}
                </h3>
              </div>
              <div className="flex items-start gap-3">
                {partnerPhoto ? (
                  <img
                    className="rounded-full w-14 h-14 border-2 border-white shadow-sm dark:shadow-gray-900/50 object-cover"
                    src={partnerPhoto}
                    alt={`${props.partner?.firstname} ${props.partner?.lastname}`}
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-blue-200 dark:bg-blue-800 flex items-center justify-center flex-shrink-0">
                    <User className="h-7 w-7 text-blue-600 dark:text-blue-400" />
                  </div>
                )}
                <div className="flex-1 space-y-1">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">
                    {props.partner?.firstname} {props.partner?.lastname}
                  </p>
                  {props.partner?.email && (
                    <a
                      href={`mailto:${props.partner.email}`}
                      className="flex items-center gap-1.5 text-sm text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      <span className="truncate">{props.partner.email}</span>
                    </a>
                  )}
                  {(props.partner?.phone || props.partner?.mobile) && (
                    <a
                      href={`tel:${
                        props.partner.phone || props.partner.mobile
                      }`}
                      className="flex items-center gap-1.5 text-sm text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {props.partner.phone || props.partner.mobile}
                    </a>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-center justify-center">
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Kein Partner zugewiesen
              </p>
            </div>
          )}
        </div>

        {/* Customer Card - Only for partner, internal, admin */}
        {showCustomer && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <User className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                Kunde
              </h3>
            </div>
            <div className="flex items-start gap-3">
              {props.person?.photoUrl ? (
                <img
                  className="rounded-full w-14 h-14 border-2 border-white shadow-sm dark:shadow-gray-900/50 object-cover"
                  src={props.person.photoUrl}
                  alt={`${firstname} ${lastname}`}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.remove(
                      'hidden',
                    );
                  }}
                />
              ) : null}
              <div
                className={`w-14 h-14 rounded-full bg-amber-200 dark:bg-amber-800 flex items-center justify-center flex-shrink-0 ${
                  props.person?.photoUrl ? 'hidden' : ''
                }`}
              >
                <User className="h-7 w-7 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="flex-1 space-y-1">
                <p className="font-semibold text-gray-900 dark:text-gray-100">
                  {[
                    firstname,
                    lastname,
                  ]
                    .filter(Boolean)
                    .join(' ') || 'Kunde'}
                </p>
                {props.person?.email && (
                  <a
                    href={`mailto:${props.person.email}`}
                    className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 dark:text-gray-100 dark:hover:text-gray-100"
                  >
                    <Mail className="h-3.5 w-3.5" />
                    <span className="truncate">{props.person.email}</span>
                  </a>
                )}
                {(props.person?.phone || props.person?.mobile) && (
                  <a
                    href={`tel:${props.person.phone || props.person.mobile}`}
                    className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 dark:text-gray-100 dark:hover:text-gray-100"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {props.person.phone || props.person.mobile}
                  </a>
                )}
                {props.person?.address && (
                  <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">
                      {props.person.address}
                      {props.person.zip &&
                        props.person.city &&
                        `, ${props.person.zip} ${props.person.city}`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Property & Evaluation Combined Card */}
        {props.property && (
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Home className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 dark:text-gray-300">
                  {isUser ? 'Ihre Immobilie' : 'Objekt'}
                </h3>
              </div>
              {canRefreshEvaluation && props.realEstateLocationId && (
                <button
                  onClick={handleRefresh}
                  disabled={refreshMutation.isPending}
                  className="flex items-center gap-1 px-1.5 py-0.5 font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-300 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
                  title="Bewertung aktualisieren"
                >
                  <RefreshCw
                    className={`h-2.5 w-2.5 ${
                      refreshMutation.isPending ? 'animate-spin' : ''
                    }`}
                  />
                  <span className="hidden text-[11px] sm:inline">
                    Aktualisieren
                  </span>
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Property Info */}
              <div className="space-y-1.5">
                {props.property.address && (
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {props.property.address}
                    {props.property.zip &&
                      props.property.city &&
                      `, ${props.property.zip} ${props.property.city}`}
                  </p>
                )}
                {props.property.price != null && (
                  <div className="flex items-center gap-2 h-5">
                    <span className="text-xs text-gray-500 dark:text-gray-400 w-20 flex-shrink-0">
                      Verkaufspreis:
                    </span>
                    <span className="text-sm font-semibold text-emerald-700 truncate">
                      {formatCurrency(Number(props.property.price))}
                    </span>
                  </div>
                )}
              </div>

              {/* Evaluation Info */}
              {canRefreshEvaluation && (
                <div className="border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-700 pt-3 md:pt-0 md:pl-4">
                  <EvaluationDisplay />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Button for customers */}
        {isUser && props.link && (
          <AppLink
            to={props.link}
            className="self-start"
          >
            <Button
              primary
              showArrow
            >
              Kontaktdaten bearbeiten
            </Button>
          </AppLink>
        )}
      </div>
    </WrapperWidget>
  );
}
